import React, { useEffect, useRef, useState } from "react";
import { LuSearch, LuX, LuLoaderCircle } from "react-icons/lu";
import api from "../../interceptor/Interceptor";
import { SEARCH_WEB } from "../../api/ApiEndpoints";
import SearchResultsPanel from "./SearchResultsPanel";
import SearchPreview from "./SearchPreview";

const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 500;
const RESULT_COUNT = 8;

export default function GlobalSearch() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selectedResult, setSelectedResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const debounceRef = useRef(null);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    const trimmedQuery = query.trim();
    if (trimmedQuery.length < MIN_QUERY_LENGTH) {
      abortControllerRef.current?.abort();
      setResults([]);
      setSelectedResult(null);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    debounceRef.current = setTimeout(async () => {
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;
      try {
        const response = await api.get(SEARCH_WEB, {
          params: { q: trimmedQuery, count: RESULT_COUNT },
          signal: controller.signal,
        });

        const searchResults = response?.data?.results || [];
        setResults(searchResults);
        setError(null);
        setSelectedResult(null);
      } catch (err) {
        if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") {
          return;
        }
        console.error("Web search failed:", err);
        setResults([]);
        setSelectedResult(null);
        setError(
          err?.response?.data?.error ||
            err?.response?.data?.message ||
            "Unable to search right now.",
        );
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query]);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const handleClear = () => {
    abortControllerRef.current?.abort();
    setQuery("");
    setResults([]);
    setSelectedResult(null);
    setError(null);
    setLoading(false);
  };

  const trimmedQuery = query.trim();

  return (
    <div className="h-full w-full bg-slate-50">
      <div className="flex min-h-[calc(100vh-64px)] w-full flex-col p-3 sm:p-4 lg:h-[calc(100vh-64px)] lg:p-5">
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-[360px_minmax(0,1fr)] xl:grid-cols-[420px_minmax(0,1fr)] 2xl:grid-cols-[470px_minmax(0,1fr)]">
          <section className="flex min-h-[450px] min-w-0 flex-col gap-3 lg:min-h-0">
            <div className="relative shrink-0">
              <LuSearch
                size={16}
                className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search the web..."
                autoComplete="off"
                className="
                  h-11 w-full rounded-xl border border-slate-200 focus:ring-blue-500/10
                  bg-white pl-10 pr-10 text-[13px] text-slate-700 shadow-sm outline-none
                  transition placeholder:text-slate-400 hover:border-slate-300 focus:border-blue-500 focus:ring-2     
                "
              />

              {loading && trimmedQuery.length >= MIN_QUERY_LENGTH && (
                <LuLoaderCircle
                  size={15}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 animate-spin text-blue-500"
                />
              )}
              {!loading && query && (
                <button
                  type="button"
                  onClick={handleClear}
                  aria-label="Clear search"
                  className="absolute right-3 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                >
                  <LuX size={14} />
                </button>
              )}
            </div>

            {trimmedQuery.length > 0 &&
              trimmedQuery.length < MIN_QUERY_LENGTH && (
                <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-[12px] text-slate-400 shadow-sm">
                  Type at least {MIN_QUERY_LENGTH} characters to search.
                </div>
              )}

            {trimmedQuery.length === 0 && (
              <div className="flex flex-1 items-center justify-center rounded-xl border border-slate-200 bg-white shadow-sm">
                <div className="max-w-xs px-6 text-center">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-slate-100 text-slate-400">
                    <LuSearch size={21} />
                  </div>

                  <h3 className="mt-3 text-[14px] font-semibold text-slate-700">
                    Search the web
                  </h3>

                  <p className="mt-1 text-[12px] leading-5 text-slate-400">
                    Enter at least {MIN_QUERY_LENGTH} characters to search.
                  </p>
                </div>
              </div>
            )}

            {/* Search Results */}
            {trimmedQuery.length >= MIN_QUERY_LENGTH && (
              <SearchResultsPanel
                loading={loading}
                error={error}
                results={results}
                query={query}
                selectedResult={selectedResult}
                onSelect={(result) => {
                  setSelectedResult(result);
                }}
              />
            )}
          </section>

          <section className="min-h-[500px] min-w-0 lg:min-h-0">
            <SearchPreview result={selectedResult} />
          </section>
        </div>
      </div>
    </div>
  );
}
